import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Trash2, Pencil, Plus, X, Eye, EyeOff, ExternalLink, RefreshCw } from "lucide-react";

const BLANK = {
    name: "",
    location: "",
    bedrooms: 3,
    bathrooms: 2,
    guests: 6,
    nightly_rate: 250,
    monthly_potential: 3800,
    amenities: "",
    image: "",
    gallery: "",
    description: "",
    airbnb_url: "",
    published: true,
    order: 0,
};

export default function PropertiesAdmin({ password }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | id
    const [form, setForm] = useState(BLANK);
    const [busy, setBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [status, setStatus] = useState(null);

    const headers = { "Content-Type": "application/json", "X-Admin-Password": password };

    const loadStatus = async () => {
        try {
            const r = await fetch(`${API}/admin/sync-lodgify/status`, {
                headers: { "X-Admin-Password": password },
            });
            if (r.ok) setStatus(await r.json());
        } catch {
            /* ignore */
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/admin/properties`, { headers: { "X-Admin-Password": password } });
            if (!r.ok) throw new Error("Auth");
            setItems(await r.json());
        } catch {
            toast.error("Could not load properties.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openNew = () => {
        setForm(BLANK);
        setEditing("new");
    };

    const openEdit = (p) => {
        setForm({
            ...p,
            amenities: (p.amenities || []).join(", "),
            gallery: (p.gallery || []).join("\n"),
            airbnb_url: p.airbnb_url || "",
        });
        setEditing(p.id);
    };

    const close = () => {
        setEditing(null);
        setForm(BLANK);
    };

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const buildPayload = () => ({
        name: form.name.trim(),
        location: form.location.trim(),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        guests: Number(form.guests),
        nightly_rate: Number(form.nightly_rate),
        monthly_potential: Number(form.monthly_potential),
        amenities: form.amenities
            ? form.amenities.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
        image: form.image.trim(),
        gallery: form.gallery
            ? form.gallery.split("\n").map((s) => s.trim()).filter(Boolean)
            : [],
        description: form.description.trim(),
        airbnb_url: form.airbnb_url ? form.airbnb_url.trim() : null,
        published: !!form.published,
        order: Number(form.order) || 0,
    });

    const save = async (e) => {
        e.preventDefault();
        if (!form.name || !form.location || !form.image || !form.description) {
            toast.error("Name, location, image and description are required.");
            return;
        }
        const payload = buildPayload();
        setBusy(true);
        try {
            const url =
                editing === "new"
                    ? `${API}/admin/properties`
                    : `${API}/admin/properties/${editing}`;
            const method = editing === "new" ? "POST" : "PUT";
            const r = await fetch(url, { method, headers, body: JSON.stringify(payload) });
            if (!r.ok) {
                const t = await r.text();
                throw new Error(t);
            }
            toast.success(editing === "new" ? "Property added." : "Property updated.");
            close();
            load();
        } catch (err) {
            toast.error("Save failed. Check the fields and try again.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
        try {
            const r = await fetch(`${API}/admin/properties/${p.id}`, {
                method: "DELETE",
                headers: { "X-Admin-Password": password },
            });
            if (!r.ok) throw new Error();
            toast.success("Property deleted.");
            load();
        } catch {
            toast.error("Could not delete.");
        }
    };

    const togglePublish = async (p) => {
        try {
            const r = await fetch(`${API}/admin/properties/${p.id}`, {
                method: "PUT",
                headers,
                body: JSON.stringify({ published: !p.published }),
            });
            if (!r.ok) throw new Error();
            load();
        } catch {
            toast.error("Could not update.");
        }
    };

    const syncLodgify = async () => {
        setSyncing(true);
        try {
            const r = await fetch(`${API}/admin/sync-lodgify`, {
                method: "POST",
                headers: { "X-Admin-Password": password },
            });
            const data = await r.json();
            if (!r.ok) {
                toast.error(data.detail || "Sync failed.");
                return;
            }
            toast.success(
                `Lodgify sync: ${data.created} new, ${data.updated} updated, ${data.unchanged} unchanged${data.archived ? `, ${data.archived} archived` : ""}.`,
            );
            load();
            loadStatus();
        } catch {
            toast.error("Sync failed — couldn't reach Lodgify.");
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div data-testid="properties-admin">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                    <div className="text-ink-soft text-sm">
                        {items.length} total · {items.filter((i) => i.published).length} published
                    </div>
                    {status?.last_sync?.at && (
                        <div
                            data-testid="lodgify-sync-status"
                            className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1"
                        >
                            Lodgify auto-sync every {status.interval_minutes} min · last{" "}
                            {new Date(status.last_sync.at).toLocaleString("en-AU", {
                                hour: "2-digit",
                                minute: "2-digit",
                                day: "2-digit",
                                month: "short",
                            })}{" "}
                            {status.last_sync.ok ? "· ok" : "· failed"}
                        </div>
                    )}
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={syncLodgify}
                        disabled={syncing}
                        className="btn-ghost inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="lodgify-sync-btn"
                    >
                        <RefreshCw
                            size={14}
                            className={syncing ? "animate-spin" : ""}
                        />
                        {syncing ? "Syncing..." : "Sync from Lodgify"}
                    </button>
                    <button
                        onClick={openNew}
                        className="btn-gold inline-flex items-center gap-2"
                        data-testid="property-add-btn"
                    >
                        <Plus size={14} /> Add property
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-ink-soft py-20">Loading...</div>
            ) : items.length === 0 ? (
                <div className="text-center text-ink-soft py-20 border border-dashed border-line-soft">
                    No properties yet. Click &ldquo;Add property&rdquo; to create one.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((p) => (
                        <article
                            key={p.id}
                            className="bg-bone border border-line-soft hover:border-gold/40 transition-colors"
                            data-testid={`admin-property-${p.id}`}
                        >
                            <div className="aspect-[4/3] bg-line-soft overflow-hidden">
                                {p.image && (
                                    <img
                                        src={p.image}
                                        alt={p.name}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div>
                                        <div className="eyebrow text-ink-soft mb-1">{p.location}</div>
                                        <h3 className="font-serif text-xl text-ink leading-tight">
                                            {p.name}
                                        </h3>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        {p.lodgify_id && (
                                            <span className="text-[10px] uppercase tracking-[0.18em] text-gold border border-gold/40 px-2 py-0.5">
                                                Lodgify
                                            </span>
                                        )}
                                        {!p.published && (
                                            <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft border border-line-soft px-2 py-0.5">
                                                Hidden
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-xs text-ink-soft mb-4">
                                    {p.bedrooms} Bed · {p.bathrooms} Bath · {p.guests} Guests ·
                                    ${p.nightly_rate}/n
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => openEdit(p)}
                                        className="text-[11px] uppercase tracking-[0.18em] border border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors inline-flex items-center gap-1.5"
                                        data-testid={`property-edit-${p.id}`}
                                    >
                                        <Pencil size={11} /> Edit
                                    </button>
                                    <button
                                        onClick={() => togglePublish(p)}
                                        className="text-[11px] uppercase tracking-[0.18em] border border-line-soft px-3 py-1.5 hover:border-gold hover:text-gold transition-colors inline-flex items-center gap-1.5"
                                        data-testid={`property-toggle-${p.id}`}
                                    >
                                        {p.published ? <EyeOff size={11} /> : <Eye size={11} />}
                                        {p.published ? "Hide" : "Show"}
                                    </button>
                                    {p.airbnb_url && (
                                        <a
                                            href={p.airbnb_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] uppercase tracking-[0.18em] border border-line-soft px-3 py-1.5 hover:border-gold hover:text-gold transition-colors inline-flex items-center gap-1.5"
                                        >
                                            <ExternalLink size={11} /> Airbnb
                                        </a>
                                    )}
                                    <button
                                        onClick={() => remove(p)}
                                        className="text-[11px] uppercase tracking-[0.18em] text-red-600 border border-red-200 px-3 py-1.5 hover:bg-red-600 hover:text-cream transition-colors inline-flex items-center gap-1.5 ml-auto"
                                        data-testid={`property-delete-${p.id}`}
                                    >
                                        <Trash2 size={11} />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {editing !== null && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto">
                    <form
                        onSubmit={save}
                        className="bg-cream border border-line-soft w-full max-w-3xl my-8"
                        data-testid="property-form"
                    >
                        <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-line-soft">
                            <h3 className="font-serif text-2xl text-ink">
                                {editing === "new" ? "Add property" : "Edit property"}
                            </h3>
                            <button
                                type="button"
                                onClick={close}
                                className="text-ink-soft hover:text-ink p-1"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="px-6 md:px-10 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label="Name *" full>
                                <input
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="Modern 4BR Home · Spacious & Family-Friendly"
                                    data-testid="property-form-name"
                                />
                            </Field>
                            <Field label="Location *">
                                <input
                                    name="location"
                                    required
                                    value={form.location}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="Werribee, VIC"
                                    data-testid="property-form-location"
                                />
                            </Field>
                            <Field label="Order">
                                <input
                                    name="order"
                                    type="number"
                                    value={form.order}
                                    onChange={onChange}
                                    className="input-line"
                                />
                            </Field>
                            <Field label="Bedrooms">
                                <input
                                    name="bedrooms"
                                    type="number"
                                    min="0"
                                    value={form.bedrooms}
                                    onChange={onChange}
                                    className="input-line"
                                />
                            </Field>
                            <Field label="Bathrooms">
                                <input
                                    name="bathrooms"
                                    type="number"
                                    min="0"
                                    value={form.bathrooms}
                                    onChange={onChange}
                                    className="input-line"
                                />
                            </Field>
                            <Field label="Max guests">
                                <input
                                    name="guests"
                                    type="number"
                                    min="1"
                                    value={form.guests}
                                    onChange={onChange}
                                    className="input-line"
                                />
                            </Field>
                            <Field label="Nightly rate ($)">
                                <input
                                    name="nightly_rate"
                                    type="number"
                                    min="0"
                                    value={form.nightly_rate}
                                    onChange={onChange}
                                    className="input-line"
                                />
                            </Field>
                            <Field label="Monthly potential ($)" full>
                                <input
                                    name="monthly_potential"
                                    type="number"
                                    min="0"
                                    value={form.monthly_potential}
                                    onChange={onChange}
                                    className="input-line"
                                />
                            </Field>
                            <Field label="Hero image URL *" full>
                                <input
                                    name="image"
                                    required
                                    value={form.image}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="https://..."
                                    data-testid="property-form-image"
                                />
                            </Field>
                            <Field label="Gallery URLs (one per line)" full>
                                <textarea
                                    name="gallery"
                                    value={form.gallery}
                                    onChange={onChange}
                                    rows={4}
                                    className="input-line"
                                    placeholder={"https://...\nhttps://..."}
                                />
                            </Field>
                            <Field label="Amenities (comma-separated)" full>
                                <input
                                    name="amenities"
                                    value={form.amenities}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="Wi-Fi, Free parking, Pets allowed"
                                />
                            </Field>
                            <Field label="Description *" full>
                                <textarea
                                    name="description"
                                    required
                                    value={form.description}
                                    onChange={onChange}
                                    rows={4}
                                    className="input-line"
                                    placeholder="A spacious, stylish 4-bedroom home..."
                                />
                            </Field>
                            <Field label="Airbnb URL (optional)" full>
                                <input
                                    name="airbnb_url"
                                    value={form.airbnb_url}
                                    onChange={onChange}
                                    className="input-line"
                                    placeholder="https://www.airbnb.com.au/rooms/..."
                                />
                            </Field>
                            <Field label="Visibility" full>
                                <label className="flex items-center gap-3 mt-1">
                                    <input
                                        type="checkbox"
                                        name="published"
                                        checked={!!form.published}
                                        onChange={onChange}
                                        className="w-4 h-4 accent-[#C9A776]"
                                        data-testid="property-form-published"
                                    />
                                    <span className="text-sm text-ink">
                                        Published (visible on the public Properties page)
                                    </span>
                                </label>
                            </Field>
                        </div>
                        <div className="px-6 md:px-10 py-5 border-t border-line-soft flex gap-3 justify-end">
                            <button type="button" onClick={close} className="btn-ghost">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={busy}
                                className="btn-gold disabled:opacity-60"
                                data-testid="property-form-save"
                            >
                                {busy ? "Saving..." : "Save property"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Field({ label, full, children }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="eyebrow text-ink-soft block mb-2">{label}</label>
            {children}
        </div>
    );
}
